import {
  AuthState,
  AUTH_STATE_CHANGE_EVENT,
  UI_AUTH_CHANNEL,
} from '@aws-amplify/ui-components';
import { Hub } from 'aws-amplify';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useAmplifyAuth } from '@/shared/hooks/useAmplifyAuth';
import { useUserInfo } from '@/shared/hooks/useUserInfo';

type State = {
  pageStatus: 'INIT' | 'CONFIRM' | 'POST_PROCESS' | 'COMPLETE';
};

type Action =
  | {
      type: 'INITIALIZED';
    }
  | {
      type: 'API_CALLED';
    }
  | {
      type: 'COMPLETE_POST_PROCESS';
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'INITIALIZED': {
      return {
        ...state,
        pageStatus: 'CONFIRM',
      };
    }

    case 'API_CALLED': {
      return {
        ...state,
        pageStatus: 'POST_PROCESS',
      };
    }

    case 'COMPLETE_POST_PROCESS': {
      return {
        ...state,
        pageStatus: 'COMPLETE',
      };
    }
  }
};

type UseLogoutConfirm = {
  state: State;
  invokeLogout: () => Promise<void>;
  isLoading: boolean;
};

export function useLogoutConfirm(): UseLogoutConfirm {
  const router = useRouter();
  const [state, dispatch] = React.useReducer(reducer, {
    pageStatus: 'INIT',
  });
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { signOut } = useAmplifyAuth();

  const { isLoadedUserInfo, userInfo } = useUserInfo();

  const invokeLogout: UseLogoutConfirm['invokeLogout'] =
    React.useCallback(async () => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      signOut().then(() => {
        dispatch({
          type: 'API_CALLED',
        });
      });
    }, [isLoading, signOut]);

  React.useEffect(() => {
    switch (state.pageStatus) {
      case 'INIT': {
        if (!isLoadedUserInfo) {
          return;
        }

        if (!userInfo.isLoggedIn) {
          router.replace('/');
          return;
        }

        dispatch({
          type: 'INITIALIZED',
        });
        return;
      }

      case 'POST_PROCESS': {
        userInfo.isLoggedIn
          ? Hub.dispatch(UI_AUTH_CHANNEL, {
              event: AUTH_STATE_CHANGE_EVENT,
              message: AuthState.SignOut,
            })
          : dispatch({
              type: 'COMPLETE_POST_PROCESS',
            });
        return;
      }

      case 'COMPLETE': {
        router.replace('/');
        return;
      }
    }
  }, [isLoadedUserInfo, router, state.pageStatus, userInfo.isLoggedIn]);

  return {
    state,
    invokeLogout,
    isLoading,
  };
}
